import { Activity } from '../types';
import { SportType } from '../types/SportType';
import {
  WEEKNUMBER,
  getDayWeek,
  getDayYearNumberFromDate,
  getHour,
  getMinutsSecondsSpeed,
  getTimeStamp,
  getWeekNumber,
} from './date';
import { roundNumber } from './maths';
import { getTSS } from './metrics';

const getActivityKey = (year: number, week: number) => {
  return Number(`${year}${week < 10 ? `0${week}` : week}`);
};

// par jour de l'année
export const getActivitiesPerday = (
  acc: { [key: number]: Activity[] },
  activity: Activity
) => {
  const day = getDayYearNumberFromDate(activity.start_date_local);
  if (acc[day]) {
    acc[day] = [...acc[day], activity];
  } else {
    acc[day] = [activity];
  }
  return acc;
};

export const getActivityTotalTimePerWeek = (
  acc: { [key: number]: number },
  activity: CustomActivity
) => {
  const { week, year } = activity;
  if (week > WEEKNUMBER) return acc;
  const key = getActivityKey(year, week);
  if (acc[key]) {
    acc[key] = roundNumber(acc[key] + activity.moving_time);
  } else {
    acc[key] = roundNumber(activity.moving_time);
  }
  return acc;
};

export const getActivityTotalDistancePerWeek = (
  acc: { [key: string]: number },
  activity: CustomActivity
) => {
  const { week, year } = activity;
  // on retire la 53e semaine qui correspond aux premiers jours de l'année mais faisant partis de la dernière semaine de l'année précédente
  // la semaine 1 commence au 1er lundi de l'année et pas au 1er janvier
  if (week > WEEKNUMBER) return acc;
  const key = getActivityKey(year, week); // 202308 pour août 2023 - permet le tri ainsi que de gérer plusieurs années
  if (acc[key]) {
    acc[key] = roundNumber(acc[key] + activity.distance);
  } else {
    acc[key] = roundNumber(activity.distance);
  }
  return acc;
};

export const getActivityTotalElevationPerWeek = (
  acc: { [key: number]: number },
  activity: CustomActivity
) => {
  const { week, year } = activity;
  if (week > WEEKNUMBER) return acc;
  const key = getActivityKey(year, week);
  if (acc[key]) {
    acc[key] = roundNumber(acc[key] + activity.elevation_gain);
  } else {
    acc[key] = roundNumber(activity.elevation_gain);
  }
  return acc;
};

export const getActivityTotalCountPerWeek = (
  acc: { [key: number]: number },
  activity: CustomActivity
) => {
  const { week, year } = activity;
  if (week > WEEKNUMBER) return acc;
  const key = getActivityKey(year, week);
  if (acc[key]) {
    acc[key] = acc[key] + 1;
  } else {
    acc[key] = 1;
  }
  return acc;
};

export const getActivityIdsPerWeek = (
  acc: { [key: number]: number[] },
  activity: CustomActivity
) => {
  const { week, year } = activity;
  if (week > WEEKNUMBER) return acc;
  const key = getActivityKey(year, week);
  if (acc[key]) {
    acc[key] = acc[key].concat(activity.id);
  } else {
    acc[key] = [activity.id];
  }
  return acc;
};

export const getActivityTotalSufferScorePerWeek = (
  acc: { [key: number]: number },
  activity: CustomActivity
) => {
  const { week, year } = activity;
  if (week > WEEKNUMBER) return acc;
  const key = getActivityKey(year, week);
  if (acc[key]) {
    acc[key] = acc[key] + activity.suffer_score;
  } else {
    acc[key] = activity.suffer_score;
  }
  return acc;
};

export const getActivityTotalTSSPerWeek = (
  acc: { [key: number]: number },
  activity: CustomActivity
) => {
  const { week, year } = activity;
  const tss =
    getTSS({ ...activity, moving_time: activity.moving_time * 3600 }) ?? 0;
  if (week > WEEKNUMBER) return acc;
  const key = getActivityKey(year, week);
  if (acc[key]) {
    acc[key] = acc[key] + tss;
  } else {
    acc[key] = tss;
  }
  return acc;
};

export type CustomActivity = {
  distance: number;
  elevation_gain: number;
  /** 0 - 52 */
  week: number;
  moving_time: number;
  /** for RunningDayChart sorting */
  startTime: number;
  /** for RunningDayChart colors */
  suffer_score: number;
  /** for RunningDayChart tooltips */
  name: string;
  id: number;
  sport_type: SportType;
  hour: number;
  /** 0 - 6 */
  day: number;
  /** 0 - 365 */
  dayYear: number;
  year: number;
  watts: number;
  polyline?: string;
  workout_type: number;
};

export const parseAllActivities = (activity: Activity): CustomActivity => ({
  day: getDayWeek(activity.start_date_local),
  dayYear: getDayYearNumberFromDate(activity.start_date_local),
  year: new Date(activity.start_date_local).getFullYear(),
  distance: roundNumber(activity.distance / 1000),
  elevation_gain: activity.total_elevation_gain,
  hour: getHour(activity.start_date_local),
  id: activity.id,
  moving_time: activity.moving_time / 3600,
  name: activity.name,
  polyline: activity.map?.summary_polyline,
  sport_type: activity.sport_type,
  startTime: getTimeStamp(activity.start_date_local),
  suffer_score: activity.suffer_score || 0,
  watts: activity.average_watts,
  week: getWeekNumber(activity.start_date_local),
  workout_type: activity.workout_type,
});

export const getRunningActivities = (activities: CustomActivity[]) =>
  activities.filter(
    (activity) =>
      activity.sport_type === 'Run' || activity.sport_type === 'TrailRun'
  );

export const getRidingActivities = (activities: CustomActivity[]) =>
  activities.filter(
    (activity) =>
      activity.sport_type === 'Ride' || activity.sport_type === 'VirtualRide'
  );

export const getSwimmingActivities = (activities: CustomActivity[]) =>
  activities.filter((activity) => activity.sport_type === 'Swim');

export const weekNumbers = [...Array(WEEKNUMBER + 1).keys()];

// pour remplir les semaines sans activités avec 0 km et éviter un décalage
export const getInitialValue = (nb = WEEKNUMBER + 1, value?: number) => {
  let initialValue: { [key: number]: number } = {};
  [...Array(nb).keys()].forEach((week) => {
    const year = new Date().getFullYear();
    const key = Number(`${year}${week < 10 ? `0${week}` : week}`);
    initialValue[key] = value ?? 0;
  });
  return initialValue;
};

export const initialValue = getInitialValue();

export const getSpeed = (sport_type: SportType, average_speed: number) => {
  if (average_speed === 0) return 0;
  switch (sport_type) {
    case 'Run':
      const speed_run = 60 / (average_speed * 3.6); // m/s en min/km
      return getMinutsSecondsSpeed(speed_run);
    case 'Ride':
    case 'VirtualRide':
      return roundNumber(average_speed * 3.6); // m/s en km/h
    case 'Swim':
      const speed_swim = 60 / (average_speed * 36); // m/s en min/100m
      return getMinutsSecondsSpeed(speed_swim);
    default:
      return roundNumber(average_speed);
  }
};

/**
 * keep only the last elements of the object
 * @param obj
 * @param limit number
 * @returns
 */
export const filterByKeys = (obj: { [key: string]: number }, limit: number) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => Number(key) > limit)
  );
};

export const getIsRace = (workout_type: number) =>
  workout_type === 1 || workout_type === 11;
